<template>
  <v-autocomplete
    v-if="!readonly"
    ref="vAutocomplete"
    :class="customData.class"
    v-bind="customProps"
    v-on="$listeners"
  >
    <slot />
    <template
      v-for="(index, name) in $scopedSlots"
      #[name]="data"
    >
      <slot
        :name="name"
        v-bind="data"
      />
    </template>
    <template #append>
      <slot name="append" />
    </template>
    <template #append-item>
      <slot name="append-item" />
    </template>
  </v-autocomplete>
  <w-read-only-text-field
    v-else
    v-bind="customData.props"
    :error-messages="errorMessages"
    v-on="$listeners"
  />
</template>

<script>
// components
import { VAutocomplete } from 'vuetify/lib';

// mixins
import isRequired from '@/mixins/component/is-required-mixin';
import pendingAble from '@/mixins/component/pending-able-mixin';
import sortedItems from '@/mixins/component/sorted-items-mixin';
import { sortItemsModule } from '@/mixins/component/sorted-items-mixin';

// helpers
import {
  filterProps,
} from '@/utils/functional-component-utils';
import { getRequiredLabelProp } from '@/components/global_components/forms/utils/required-label-util';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import isEqual from 'lodash/isEqual';

const currentSelectionText = (props) => {
  const itemValue = props.itemValue || 'value';
  const itemText = props.itemText || 'text';
  const currentSelection = props.items.find((i) =>
    props.returnObject
      ? get(i, itemValue) == get(props.value, itemValue)
      : get(i, itemValue) == props.value
  );

  if (isFunction(itemText))
    return itemText(currentSelection);

  return get(currentSelection, itemText);
};

const compareIds = (a, b) => {
  if (isNaN(a) && !isNaN(b) || !isNaN(a) && isNaN(b)) {
    return a == b
  }
  return isEqual(a, b)
}

const compareSubstrings = (item, queryText, itemText) => {
  const substringsOfItem = itemText.replace(/,/g, '').toLowerCase().split(' ');
  const substringsOfQuery = queryText.replace(/,/g, '').toLowerCase().split(' ');

  return substringsOfQuery.every(querySubstring => 
    substringsOfItem.some(itemSubstring => itemSubstring.includes(querySubstring))
  );
};

export default {
  name: 'WorkAutocomplete',
  mixins: [isRequired, pendingAble, sortedItems],
  props: {
    ...VAutocomplete.options.props,
    dense: { type: Boolean, default: true },
    hideNoData: { type: Boolean, default: false },
    preventIconFlip: { type: Boolean, default: false },
    displayString: { type: String },
    clearIcon: { type: String, default: '$wClose' },
    appendIcon: { type: String, default: '$wArrowDown' },
    clearable: { type: Boolean, default: true },
    sort: { type: Boolean, default: true }
  },
  computed: {
    customData() {
      if (!this.readonly) {
        let customData = {
          class: {
            'w-autocomplete': true,
            'w-autocomplete--input': true,
            'w-autocomplete--clearable': this.clearable || this.appendIcon,
            'w-autocomplete--prevent-icon-flip': this.preventIconFlip
          },
          props: {
            outlined: true,
            hideDetails: 'auto',
            ...getRequiredLabelProp(this.$props),
            items: this.sort ? sortItemsModule.getSortedItems(
              this.$props,
              this.items
            ) : this.items,
            filter: compareSubstrings,
          },
        }

        if (
          this.itemValue === 'id' &&
          (this.valueComparator === undefined ||
            this.valueComparator.name === 'deepEqual')
        ) {
          customData.props.valueComparator = compareIds;
        }
        return customData;
      } else {
        return {
          props: {
            label: this.label,
            value:
              this.displayString || currentSelectionText(this.$props),
            pending: this.pending,
          },
        }
      }
    },
    customProps() {
      return { ...filterProps(this.$props, 'items'), ...this.customData.props }
    }
  },
  methods: {
    reset() {
      this.$refs.vAutocomplete.reset();
    }
  }
}

</script>

<style lang="scss">
@import '~vuetify/src/styles/styles';

.w-autocomplete {
  .v-select__selections {
    padding: calc(#{$spacer} * 2) 0 #{$spacer} !important;

    .v-chip {
      margin: #{$spacer};
      background-color: $chips-bg-color;

      &--disabled {
        color: $chips-text-color;
      }

      .v-chip__content {
        span {
          overflow: hidden;
          text-overflow: ellipsis !important;
        }

        ;
      }
    }
  }

  &.v-autocomplete.v-select--is-menu-active.w-autocomplete--prevent-icon-flip .v-input__icon--append .v-icon {
    transform: none;
  }

  &.v-input--is-focused {
    .v-select__selections input {
      margin: #{$spacer};
      padding: 0;
    }
  }

  & .v-input__append-inner {
    display: inline-flex;
    margin: auto;
    cursor: pointer;

    .v-btn,
    .v-icon {
      color: $icon-color;
      padding: 0;
      height: auto;
    }
  }

  & .v-input__append-outer {
    display: inline-flex;
    margin: auto;
    cursor: pointer;

    .v-btn,
    .v-icon {
      color: $icon-color;
      padding: 0;
      height: auto;
    }
  }

  &--input>.v-input__control {
    >.v-input__slot {
      background: $input-bg-color !important;
    }

    .v-input__slot:hover {
      background: $input-hover-color !important;
    }
  }

  &--input:not(.error--text)>.v-input__control {
    padding-top: 0.25rem;
  }

  &:not(--clearable) ::v-deep {
    .v-input__append-inner {
      display: none;
    }
  }
}
</style>
